// InlineEditInput — the in-place text editor shared by the process WYSIWYG
// edit-in-place affordances (callee retype, terminator/condition/head spans).
// A bare autofocused input: Enter commits, Escape / blur cancels (cancel-on-blur
// is deliberate — an accidental focus loss must never rewrite the model; the
// commit is always an explicit Enter). Click is swallowed so opening the editor
// doesn't also fire the cell's select handler.
//
// Pure view: no model state, no context — the caller owns the commit (which
// funnels through the step-edit api and the recompile invariant). Reused across
// surfaces so the edit path never forks.

import { useState } from "react";

export interface InlineEditInputProps {
  /** Text the input opens with (the current span value). */
  readonly initial: string;
  /** Explicit commit (Enter) — returns nothing; the caller reconciles. */
  readonly onCommit: (value: string) => void;
  /** Escape or blur — abandon without writing. */
  readonly onCancel: () => void;
  readonly placeholder?: string;
  readonly ariaLabel?: string;
  /** Extra classes (width/typography) so a caller can match its cell. */
  readonly className?: string;
}

export function InlineEditInput({ initial, onCommit, onCancel, placeholder, ariaLabel, className }: InlineEditInputProps) {
  const [v, setV] = useState(initial);
  return (
    <input
      autoFocus
      value={v}
      placeholder={placeholder}
      aria-label={ariaLabel}
      data-inline-edit-input=""
      onChange={(e) => setV(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onCommit(v);
        } else if (e.key === "Escape") {
          e.stopPropagation();
          onCancel();
        }
      }}
      onBlur={() => onCancel()}
      className={
        className ??
        "w-full box-border rounded-sm border border-sky-500 bg-slate-900 px-1 py-0.5 text-[11px] text-slate-100 text-center outline-none"
      }
    />
  );
}
